import { createSignal, onCleanup } from 'solid-js'

export type ScrollAxis = 'x' | 'y'

const scrollExtent = (axis: ScrollAxis) => (element: HTMLElement) =>
  axis === 'x' ? element.scrollWidth : element.scrollHeight

export function createScrollEdges(
  axis: ScrollAxis,
  threshold: () => number = () => 1,
  extent: (element: HTMLElement) => number = scrollExtent(axis)
) {
  const [start, setStart] = createSignal(false)
  const [end, setEnd] = createSignal(false)
  const [startDistance, setStartDistance] = createSignal(0)
  const [endDistance, setEndDistance] = createSignal(0)
  let scroller: HTMLElement | undefined

  const update = () => {
    if (!scroller) {
      return
    }
    const offset = axis === 'x' ? scroller.scrollLeft : scroller.scrollTop
    const hidden = extent(scroller) - (axis === 'x' ? scroller.clientWidth : scroller.clientHeight)
    setStart(offset > 1)
    setEnd(hidden - offset > threshold())
    setStartDistance(Math.max(0, offset))
    setEndDistance(Math.max(0, hidden - offset))
  }

  const resizes = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(update)

  const attach = (element: HTMLElement) => {
    scroller = element
    element.addEventListener('scroll', update, { passive: true })
    resizes?.observe(element)
    update()
  }

  const observe = (element: HTMLElement) => {
    resizes?.observe(element)
  }

  onCleanup(() => {
    scroller?.removeEventListener('scroll', update)
    resizes?.disconnect()
  })

  return { start, end, startDistance, endDistance, attach, observe, update }
}

export function edgeFadeMask(
  axis: ScrollAxis,
  start: boolean,
  end: boolean,
  size = '1.5rem'
): string | undefined {
  if (!start && !end) {
    return undefined
  }
  const direction = axis === 'x' ? 'to right' : 'to bottom'
  const from = start ? size : '0px'
  const to = end ? size : '0px'
  return `linear-gradient(${direction}, transparent 0, #000 ${from}, #000 calc(100% - ${to}), transparent 100%)`
}
